import React from 'react';
import { Checkbox as AntCheckbox, CheckboxProps as AntCheckboxProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { Group } = AntCheckbox;
type AntCheckboxGroupProps = React.ComponentProps<typeof Group>;

export interface CheckboxProps extends AntCheckboxProps {}

const useStyles = createUseStyles((theme: ThemeType) => {
  const getBaseCheckboxStyles = () => ({
    '& .ant-checkbox-inner': {
      width: theme.spacing[4],
      height: theme.spacing[4],
      borderColor: `${theme.color.semantic.border.primary} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.xs,
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      transition: 'all 0.2s ease',
    },
    '& .ant-checkbox:hover .ant-checkbox-inner': {
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
    },
    '& .ant-checkbox-checked .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.inverse} !important`,
      },
    },
    '& .ant-checkbox-checked:hover .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.inverse} !important`,
      },
    },
    '& .ant-checkbox-indeterminate .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      '&:after': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        border: 'none',
        height: theme.border.width.md,
        width: theme.spacing[2],
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    },
    '& .ant-checkbox-indeterminate:hover .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      '&:after': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      },
    },
    '& .ant-checkbox-input:focus + .ant-checkbox-inner': {
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
    },
    '& .ant-checkbox-disabled .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      cursor: 'not-allowed',
    },
    '& .ant-checkbox-disabled': {
      cursor: 'not-allowed',
      '& .ant-checkbox-input': {
        cursor: 'not-allowed',
      },
      '& + span': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed',
      },
    },
    '& .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
        borderWidth: theme.border.width.sm,
      },
    },
    '& .ant-checkbox-disabled.ant-checkbox-indeterminate .ant-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      '&:after': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        border: 'none',
        height: theme.border.width.md,
        width: theme.spacing[2],
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        visibility: 'visible',
      },
    },
    '& .ant-checkbox + span': {
      color: `${theme.color.semantic.text.primary} !important`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      paddingLeft: theme.spacing[2],
      display: 'inline-block',
      verticalAlign: 'middle',
    },
  });

  return {
    checkbox: {
      ...getBaseCheckboxStyles(),
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: theme.typography.lineHeight.tight,
      verticalAlign: 'middle',
      '& .ant-checkbox': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: theme.spacing[0],
        marginTop: theme.spacing[0],
        marginBottom: theme.spacing[0],
        verticalAlign: 'middle',
      },
      '& .ant-checkbox-inner': {
        display: 'inline-block',
        verticalAlign: 'middle',
      },
    },
    checkboxGroup: {
      ...getBaseCheckboxStyles(),
      display: 'flex',
      gap: theme.spacing[2],
      '&.ant-checkbox-group-horizontal': {
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      '& .ant-checkbox-wrapper': {
        marginRight: theme.spacing[0],
        marginBottom: theme.spacing[0],
      },
    },
  };
});

const CheckboxComponent: React.FC<CheckboxProps> = ({
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const combinedClassName = `${classes.checkbox} ${className}`.trim();

  return (
    <AntCheckbox
      {...rest}
      className={combinedClassName}
    />
  );
};

export interface CheckboxGroupProps extends Omit<AntCheckboxGroupProps, 'className'> {
  className?: string;
}

const CheckboxGroupComponent: React.FC<CheckboxGroupProps> = ({
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const combinedClassName = `${classes.checkboxGroup} ${className}`.trim();

  return (
    <Group
      {...rest}
      className={combinedClassName}
    />
  );
};

export const Checkbox = CheckboxComponent as typeof CheckboxComponent & {
  Group: typeof CheckboxGroupComponent;
};

Checkbox.Group = CheckboxGroupComponent;

export default Checkbox;
